import { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Wallet, Plus, Loader2, Receipt, Trash2, IndianRupee, CheckCircle2, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DiscountsPanel } from "@/components/DiscountsPanel";
import { ReceiptActions } from "@/components/ReceiptActions";

export default function FeesPage() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [schoolTypes, setSchoolTypes] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [structures, setStructures] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [students, setStudents] = useState([]);
  const [receiptInvoice, setReceiptInvoice] = useState(null);
  const [payments, setPayments] = useState([]);

  const isGlobal = user?.role === "master_admin" || user?.role === "super_admin";

  async function loadMeta() {
    const [b, s] = await Promise.all([api.get("/branches"), api.get("/meta/school-types")]);
    setBranches(b.data);
    setSchoolTypes(s.data);
    const def = user?.branch_id || b.data[0]?.id || "";
    setBranchId(def);
  }

  async function loadAll() {
    const params = {};
    if (isGlobal && branchId) params.branch_id = branchId;
    const [st, inv, sum] = await Promise.all([
      api.get("/fees/structures", { params }),
      api.get("/fees/invoices", { params }),
      api.get("/fees/summary", { params }),
    ]);
    setStructures(st.data);
    setInvoices(inv.data);
    setSummary(sum.data);
    const sParams = isGlobal && branchId ? { branch_id: branchId } : {};
    const stu = await api.get("/students", { params: sParams });
    setStudents(stu.data);
  }

  useEffect(() => { loadMeta().catch(() => {}); }, []);
  useEffect(() => { if (branchId) loadAll().catch(() => {}); }, [branchId]); // eslint-disable-line

  async function openReceipt(inv) {
    setReceiptInvoice(inv);
    const { data } = await api.get(`/fees/invoices/${inv.id}/payments`);
    setPayments(data);
  }

  async function refreshReceipt() {
    const { data } = await api.get(`/fees/invoices/${receiptInvoice.id}/receipt`);
    setReceiptInvoice((cur) => ({ ...cur, ...data.invoice }));
    setPayments(data.payments);
    loadAll();
  }

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-2">Finance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Fees & Billing</h1>
          <p className="text-slate-500 mt-1.5">Set fee structures, raise invoices and collect payments.</p>
        </div>
        {isGlobal && (
          <Select value={branchId} onValueChange={setBranchId}>
            <SelectTrigger data-testid="fees-branch-filter" className="w-full sm:w-64 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI label="Total Billed" value={fmt(summary?.total_billed)} icon={Wallet} accent="from-violet-500 to-purple-700" testid="kpi-billed" />
        <KPI label="Collected" value={fmt(summary?.total_paid)} icon={CheckCircle2} accent="from-emerald-500 to-teal-600" testid="kpi-paid" />
        <KPI label="Pending Dues" value={fmt(summary?.total_due)} icon={AlertTriangle} accent="from-rose-500 to-pink-600" testid="kpi-due" />
        <KPI label="Open Invoices" value={(summary?.pending ?? 0) + (summary?.partial ?? 0)} icon={Receipt} accent="from-indigo-500 to-violet-700" testid="kpi-open" />
      </div>

      <Tabs defaultValue="invoices" className="w-full">
        <TabsList className="bg-violet-100">
          <TabsTrigger value="invoices" data-testid="tab-invoices" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Invoices & Payments</TabsTrigger>
          <TabsTrigger value="structures" data-testid="tab-structures" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Fee Structures</TabsTrigger>
          <TabsTrigger value="discounts" data-testid="tab-discounts" className="data-[state=active]:bg-white data-[state=active]:text-violet-900">Discounts</TabsTrigger>
        </TabsList>

        {/* Invoices */}
        <TabsContent value="invoices" className="mt-5">
          <div className="flex justify-end mb-3">
            <InvoiceDialog students={students} structures={structures} onCreated={loadAll} />
          </div>
          {invoices.length === 0 ? (
            <Card className="border-dashed border-2 border-violet-200"><CardContent className="py-16 text-center text-slate-500">No invoices yet. Click <em>New Invoice</em> to raise one.</CardContent></Card>
          ) : (
            <Card className="border-violet-100 overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto scrollbar-thin">
                  <table className="w-full text-sm">
                    <thead className="bg-violet-50 text-slate-700">
                      <tr>
                        <th className="text-left px-4 py-3 font-semibold">Receipt</th>
                        <th className="text-left px-3 py-3 font-semibold">Student</th>
                        <th className="text-left px-3 py-3 font-semibold">Period</th>
                        <th className="text-right px-3 py-3 font-semibold">Total</th>
                        <th className="text-right px-3 py-3 font-semibold">Paid</th>
                        <th className="text-right px-3 py-3 font-semibold">Balance</th>
                        <th className="text-center px-3 py-3 font-semibold">Status</th>
                        <th className="px-3 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {invoices.map((i) => (
                        <tr key={i.id} data-testid={`invoice-${i.receipt_no}`}>
                          <td className="px-4 py-3 font-mono text-xs text-slate-600">{i.receipt_no}</td>
                          <td className="px-3 py-3">
                            <div className="font-semibold text-slate-900">{i.student_name}</div>
                            <div className="text-xs text-slate-500">{i.admission_number} • {i.class_name}/{i.section}</div>
                          </td>
                          <td className="px-3 py-3">{i.period}</td>
                          <td className="text-right px-3 py-3 font-semibold">₹{i.total.toFixed(0)}</td>
                          <td className="text-right px-3 py-3 text-emerald-700 font-semibold">₹{i.paid.toFixed(0)}</td>
                          <td className="text-right px-3 py-3 text-rose-700 font-semibold">₹{i.balance.toFixed(0)}</td>
                          <td className="text-center px-3 py-3">
                            <Badge className={statusColor(i.status)}>{i.status}</Badge>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <Button data-testid={`open-receipt-${i.receipt_no}`} onClick={() => openReceipt(i)} variant="outline" size="sm" className="border-violet-200 text-violet-700 hover:bg-violet-50">
                              <Receipt size={14} className="mr-1" /> Open
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Structures */}
        <TabsContent value="structures" className="mt-5">
          <div className="flex justify-end mb-3">
            <StructureDialog branches={branches} branchId={branchId} schoolTypes={schoolTypes} onCreated={loadAll} />
          </div>
          {structures.length === 0 ? (
            <Card className="border-dashed border-2 border-violet-200"><CardContent className="py-16 text-center text-slate-500">No fee structures yet.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {structures.map((s) => (
                <Card key={s.id} data-testid={`structure-${s.id}`} className="border-violet-100">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100">{s.class_name}</Badge>
                      <Badge variant="outline">{s.academic_year}</Badge>
                      {s.yearly_fee > 0 && <Badge data-testid={`structure-yearly-${s.id}`} className="bg-violet-100 text-violet-800 hover:bg-violet-100">Yearly {fmt(s.yearly_fee)}</Badge>}
                    </div>
                    <div className="mt-3 space-y-1.5">
                      {s.items.map((it, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-slate-700">{it.name}</span>
                          <span className="font-semibold">₹{Number(it.amount).toFixed(0)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-slate-100 flex justify-between items-center">
                      <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total</span>
                      <span className="font-display text-xl font-bold text-violet-700">₹{s.total.toFixed(0)}</span>
                    </div>
                    <button
                      data-testid={`delete-structure-${s.id}`}
                      onClick={async () => { if (window.confirm("Delete this structure?")) { await api.delete(`/fees/structures/${s.id}`); toast.success("Deleted"); loadAll(); } }}
                      className="mt-3 text-xs text-slate-400 hover:text-red-600 flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      <TabsContent value="discounts" className="mt-5">
          <DiscountsPanel students={students} structures={structures} branchId={branchId} />
        </TabsContent>
      </Tabs>

      {/* Receipt / payment dialog */}
      <Dialog open={!!receiptInvoice} onOpenChange={(v) => { if (!v) { setReceiptInvoice(null); setPayments([]); } }}>
        <DialogContent className="max-w-xl">
          {receiptInvoice && (
            <ReceiptPanel key={receiptInvoice.id + receiptInvoice.balance} invoice={receiptInvoice} payments={payments} onPaid={refreshReceipt} />
          )}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function fmt(v) {
  if (v == null) return "—";
  return "₹" + Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function statusColor(s) {
  if (s === "paid") return "bg-emerald-100 text-emerald-800 hover:bg-emerald-100 capitalize";
  if (s === "partial") return "bg-amber-100 text-amber-800 hover:bg-amber-100 capitalize";
  return "bg-rose-100 text-rose-800 hover:bg-rose-100 capitalize";
}

function KPI({ label, value, icon: Icon, accent, testid }) {
  return (
    <Card data-testid={testid} className="border-violet-100 shadow-sm">
      <CardContent className="p-5 flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
          <div className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1.5">{value}</div>
        </div>
        <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center text-white shadow`}>
          <Icon size={18} />
        </div>
      </CardContent>
    </Card>
  );
}

function InvoiceDialog({ students, structures, onCreated }) {
  const [open, setOpen] = useState(false);
  const [studentId, setStudentId] = useState("");
  const [period, setPeriod] = useState("Nov 2025");
  const [due, setDue] = useState(new Date(Date.now() + 10 * 86400000).toISOString().slice(0, 10));
  const [items, setItems] = useState([{ name: "Tuition", amount: 5000 }]);
  const [saving, setSaving] = useState(false);

  const student = students.find((s) => s.id === studentId);
  const matchingStructure = student && structures.find((st) => st.class_name === student.class_name);

  useEffect(() => {
    if (matchingStructure) setItems(matchingStructure.items.map((i) => ({ ...i })));
  }, [matchingStructure]);

  const total = items.reduce((a, b) => a + (Number(b.amount) || 0), 0);

  async function submit(e) {
    e.preventDefault();
    if (!studentId) return;
    setSaving(true);
    try {
      await api.post("/fees/invoices", { student_id: studentId, period, items, due_date: due });
      toast.success("Invoice created");
      setOpen(false);
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-invoice-btn" className="bg-violet-600 hover:bg-violet-700 font-semibold">
          <Plus size={16} className="mr-1.5" /> New Invoice
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-xl">Raise an invoice</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div>
            <Label>Student</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="invoice-student-select" className="mt-1.5"><SelectValue placeholder="Choose a student" /></SelectTrigger>
              <SelectContent>
                {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} • {s.admission_number}</SelectItem>)}
              </SelectContent>
            </Select>
            {matchingStructure && <p className="text-xs text-violet-700 mt-1">Pre-filled from {student.class_name} structure</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Period</Label>
              <Input data-testid="invoice-period" value={period} onChange={(e) => setPeriod(e.target.value)} className="mt-1.5 input-focus" required />
            </div>
            <div>
              <Label>Due date</Label>
              <Input data-testid="invoice-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} className="mt-1.5 input-focus" required />
            </div>
          </div>
          <div>
            <Label>Line items</Label>
            <div className="space-y-2 mt-1.5">
              {items.map((it, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input value={it.name} onChange={(e) => { const n = [...items]; n[idx].name = e.target.value; setItems(n); }} placeholder="Description" className="flex-1 input-focus" />
                  <Input type="number" value={it.amount} onChange={(e) => { const n = [...items]; n[idx].amount = Number(e.target.value); setItems(n); }} placeholder="Amount" className="w-32 input-focus" />
                  <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600">
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => setItems([...items, { name: "", amount: 0 }])} className="text-sm text-violet-600 hover:text-violet-800 font-medium">+ Add line</button>
            </div>
          </div>
          <div className="flex justify-between items-center pt-2 border-t border-slate-100">
            <span className="text-sm text-slate-500">Total <span className="text-xs">(approved student discount is applied automatically)</span></span>
            <span className="font-display text-2xl font-bold text-violet-700">₹{total.toFixed(0)}</span>
          </div>
          <Button data-testid="save-invoice-btn" type="submit" disabled={saving || !studentId} className="w-full h-11 bg-violet-600 hover:bg-violet-700">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Create Invoice"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function StructureDialog({ branches, branchId, schoolTypes, onCreated }) {
  const [open, setOpen] = useState(false);
  const branch = branches.find((b) => b.id === branchId);
  const schoolType = branch?.school_type;
  const classes = schoolTypes.find((s) => s.id === schoolType)?.classes || [];
  const [className, setClassName] = useState("");
  const [year, setYear] = useState("2025-26");
  const [items, setItems] = useState([{ name: "Tuition", amount: 5000 }, { name: "Transport", amount: 1500 }]);
  const [yearlyFee, setYearlyFee] = useState(0);
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/fees/structures", { branch_id: branchId, school_type: schoolType, class_name: className, academic_year: year, items, yearly_fee: Number(yearlyFee) || 0 });
      toast.success("Structure saved");
      setOpen(false);
      onCreated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-structure-btn" className="bg-violet-600 hover:bg-violet-700 font-semibold">
          <Plus size={16} className="mr-1.5" /> New Structure
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display text-xl">Fee structure</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Class</Label>
              <Select value={className} onValueChange={setClassName}>
                <SelectTrigger data-testid="structure-class" className="mt-1.5"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>{classes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Academic year</Label>
              <Input data-testid="structure-year" value={year} onChange={(e) => setYear(e.target.value)} className="mt-1.5 input-focus" />
            </div>
          </div>
          <div>
            <Label>Yearly fee for this class (per student, ₹)</Label>
            <Input data-testid="structure-yearly-fee" type="number" min={0} value={yearlyFee} onChange={(e) => setYearlyFee(e.target.value)} className="mt-1.5 input-focus" />
            <p className="text-xs text-slate-500 mt-1">Total for the whole academic year. Fee heads below are what each invoice is pre-filled with.</p>
          </div>
          <div>
            <Label>Fee heads</Label>
            <div className="space-y-2 mt-1.5">
              {items.map((it, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input value={it.name} onChange={(e) => { const n = [...items]; n[idx].name = e.target.value; setItems(n); }} className="flex-1 input-focus" />
                  <Input type="number" value={it.amount} onChange={(e) => { const n = [...items]; n[idx].amount = Number(e.target.value); setItems(n); }} className="w-32 input-focus" />
                  <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-red-600"><Trash2 size={16} /></button>
                </div>
              ))}
              <button type="button" onClick={() => setItems([...items, { name: "", amount: 0 }])} className="text-sm text-violet-600 font-medium">+ Add fee head</button>
            </div>
          </div>
          <Button data-testid="save-structure-btn" type="submit" disabled={saving || !className} className="w-full h-11 bg-violet-600 hover:bg-violet-700">
            {saving ? <Loader2 className="animate-spin" size={18} /> : "Save Structure"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptPanel({ invoice, payments, onPaid }) {
  const { user } = useAuth();
  const [amount, setAmount] = useState(invoice.balance);
  const [mode, setMode] = useState("upi");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);
  const canPay = ["master_admin", "super_admin", "accountant"].includes(user?.role);

  async function pay() {
    setSaving(true);
    try {
      await api.post(`/fees/invoices/${invoice.id}/payments`, { amount: Number(amount), mode, reference: ref });
      toast.success(`₹${Number(amount).toFixed(0)} collected`);
      onPaid?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display text-xl flex items-center gap-2">
          <Receipt size={20} /> {invoice.receipt_no}
        </DialogTitle>
      </DialogHeader>
      <div className="pt-2">
        <div className="rounded-xl bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-100 p-4 mb-4">
          <div className="text-sm text-slate-600">{invoice.student_name} • {invoice.admission_number} • {invoice.class_name}/{invoice.section}</div>
          <div className="text-xs text-slate-500 mt-1">{invoice.period} • Due {invoice.due_date}</div>
          <div className="mt-3 space-y-1">
            {invoice.items.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span>{it.name}</span>
                <span className="font-semibold">₹{Number(it.amount).toFixed(0)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-violet-200 flex justify-between items-center">
            <div className="text-sm text-slate-600">Balance Due</div>
            <div className="font-display text-2xl font-bold text-rose-700">₹{invoice.balance.toFixed(0)}</div>
          </div>
        </div>

        {payments.length > 0 && (
          <div className="mb-4">
            <div className="text-xs uppercase font-semibold text-slate-500 tracking-wider mb-2">Payment History</div>
            <div className="space-y-1">
              {payments.map((p) => (
                <div key={p.id} className="flex justify-between text-sm bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                  <span><span className="font-mono text-xs text-slate-500">{p.receipt_no}</span> • {p.mode.toUpperCase()}{p.reference ? ` • ${p.reference}` : ""}</span>
                  <span className="font-semibold text-emerald-700">₹{p.amount.toFixed(0)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {canPay && invoice.balance > 0 && (
          <div className="border-t border-slate-100 pt-4">
            <Label className="text-xs uppercase font-semibold text-slate-500 tracking-wider">Collect payment</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              <Input data-testid="pay-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-focus" placeholder="Amount" />
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="pay-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="bank">Bank Transfer</SelectItem>
                </SelectContent>
              </Select>
              <Input data-testid="pay-ref" value={ref} onChange={(e) => setRef(e.target.value)} className="input-focus" placeholder="Ref (optional)" />
            </div>
            <Button data-testid="pay-submit" onClick={pay} disabled={saving || Number(amount) <= 0} className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700">
              <IndianRupee size={16} className="mr-1" /> Collect ₹{Number(amount).toFixed(0)}
            </Button>
          </div>
        )}
        <ReceiptActions invoiceId={invoice.id} />
      </div>
    </>
  );
}
